// Pure billing logic for a booking's Beziehung (tenant relation): turns the
// relation's Leistungen into one-off invoice positions for a FIXED-END booking
// (mirrors the legacy split — open-ended relations are billed by the subscription
// scheduler from the relation row, so they emit no positions here).
//
// Side-effect free (imports only the pure proportional-time helper, no facade /
// HTTP / Angular) → trivially unit testable, unlike the payload mapper whose
// vesting-period helper drags in an Angular api-service. The payload mapper just
// spreads the result into its `positions` array.

import { calculateProportionalTime } from '@shared/functions/calculate-proportional-time';
import { DurationUnit, getDurationFromDates } from '@shared/functions/get-duration-from-dates';
import { DefaultBerthReservationTimeUnit } from '@shared/models/bcm-settings';
import { PayableOption } from '@shared/models/payable-option';
import { resolveBerthProductQuantity } from '@shared/utils/berth-product-booking.util';
import { hasUnmatchedPriceRuleShape } from '@shared/functions/resolve-dynamic-price-state';
import { DEFAULT_BERTH_RESERVATION_UNIT } from '@modules/bcm/settings/default-units/default-units.component';

import {
    BookingV2RelationLineOverride,
    BookingV2RelationLineOverrides,
    BookingV2RelationOption,
    BookingV2RelationProduct,
} from '../dtos/booking-v2.dtos';
// Type-only (erased at runtime) → keeps this module free of the payload mapper's
// Angular-tainted import graph while reusing its position shape.
import type { BookingV2PositionDto } from '../services/booking-v2-payload.mapper';

// The quantity one relation product is billed with. Day/overnight units take the
// stay duration — the same rule berth products and the legacy dialog follow
// (BookingDialogPositions.resolveTenantRelationQuantity); every other unit keeps
// the relation's configured quantity scaled by the billing intervals. Shared by
// the billed position and both previews so they cannot drift. (#7017)
export function relationProductQuantity(
    product: BookingV2RelationProduct,
    from: Date | null,
    to: Date | null,
    payableOption: PayableOption | null,
    timeUnit: DefaultBerthReservationTimeUnit | null,
): number {
    const proportional = from && to && payableOption
        ? (calculateProportionalTime(from, to, payableOption) ?? 1)
        : 1;

    const durationUnit = timeUnit?.type === DEFAULT_BERTH_RESERVATION_UNIT.OVERNIGHT_STAY
        ? DurationUnit.Overnights
        : DurationUnit.Days;

    const durationOfStay = getDurationFromDates(from, to, durationUnit, timeUnit) ?? 0;

    return resolveBerthProductQuantity(
        product.quantity * proportional,
        durationOfStay,
        product.unitUniqueName,
        false,
    );
}

// Whether the product's own price rules exclude this booking context, in which
// case it must not be billed at all: without a match the engine answers with the
// product's catalog price, so the line looks like any other and silently adds
// money for a product the rules ruled out. Same verdict as the legacy dialog's.
// (#7017)
export function relationProductIsUnbillable(product: BookingV2RelationProduct): boolean {
    return !!product.hasDynamicPrice
        && !!product.dynamicPrice
        && hasUnmatchedPriceRuleShape(product.priceRuleShape, product.dynamicPrice);
}

// The Bezeichnung the rule gives the line: the matched rule (group) name, like the
// Bierdeckel and the legacy dialog (#6990); the product name otherwise. (#7044)
export function relationProductAutoTitle(product: BookingV2RelationProduct): string {
    return (product.hasDynamicPrice && product.dynamicRuleName) || product.name;
}

// Card, Rechnungsversand and the billed position all read title and unit price
// through these two, so a hand edit shows up everywhere alike. (#7044)
export function relationProductTitle(
    product: BookingV2RelationProduct,
    override?: BookingV2RelationLineOverride | null,
): string {
    return override?.title ?? relationProductAutoTitle(product);
}

export function relationProductUnitPrice(
    product: BookingV2RelationProduct,
    override?: BookingV2RelationLineOverride | null,
): number {
    return override?.price ?? product.price;
}

// The relation's Leistungen as one-off invoice positions — ONLY for a fixed-end
// booking. The quantity is scaled by `calculateProportionalTime`, i.e. the number
// of whole billing intervals the booking span covers, rounded up and at least 1
// (a yearly product on a sub-year booking still bills one full year). Each line is
// flagged `fromTenantRelation` so the backend links it to the relation row.
//
// Edit: the previously-saved relation positions are removed and regenerated (their
// ids are tracked separately from editable services — they are NOT free services).
// When the relation is not yet enriched (products undefined, edit load race), the
// existing positions are left untouched (neither removed nor recreated). Hand edits
// survive the regeneration as `overrides` (recovered on load, #7044).
export function mapTenantRelationPositions(
    relation: BookingV2RelationOption | null,
    from: Date | null,
    to: Date | null,
    isEdit: boolean,
    baselineRelationPositionIds: readonly number[],
    timeUnit: DefaultBerthReservationTimeUnit | null = null,
    overrides: BookingV2RelationLineOverrides = {},
): BookingV2PositionDto[] {
    // Relation present but its products are not loaded yet (edit enrichment
    // pending) → don't touch the existing positions.
    if (relation && relation.products === undefined) {
        return [];
    }

    const result: BookingV2PositionDto[] = [];

    if (relation && to && relation.products?.length) {
        const payableOption = relation.payableOption ?? null;

        for (const p of relation.products) {
            if (relationProductIsUnbillable(p)) {
                continue;
            }

            result.push({
                product: { id: p.productId },
                unit: { id: p.unitId },
                taxRate: { id: p.taxRateId },
                quantity: relationProductQuantity(p, from, to, payableOption, timeUnit),
                price: relationProductUnitPrice(p, overrides[p.productId]),
                title: relationProductTitle(p, overrides[p.productId]),
                // The locks mark a hand edit, so the next load reads it back and product updates leave it be.
                priceLock: overrides[p.productId]?.price !== undefined,
                titleLock: overrides[p.productId]?.title !== undefined,
                discountPercentage: null,
                vestingPeriodFrom: from,
                vestingPeriodUntil: to,
                fromTenantRelation: true,
                // Carry the evaluated dynamic price so the backend persists the rule
                // provenance; omitted for non-rule products (keeps the position lean).
                ...(p.dynamicPrice ? { dynamicPrice: p.dynamicPrice } : {}),
            });
        }
    }

    // Edit: drop the prior relation positions (regenerated above, or gone when the
    // relation is now open-ended/removed). Paid ones are not in this baseline.
    if (isEdit) {
        for (const id of baselineRelationPositionIds) {
            result.push({ id, deleted: true } as unknown as BookingV2PositionDto);
        }
    }

    return result;
}
